import { useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorBanner,
  Field,
  Input,
  Money,
  Progress,
  Select,
  Skeleton,
  Stat,
  StatusBadge,
  useAction,
  useApiQuery,
  useCreate,
  useInvalidateResource,
  useResourceList,
  type ScreenContext,
} from '../../engine';
import { MoneyEditor } from '../../engine/fields/registry';
import { formatMoney, formatQty, formatRelativeDays, toMinorInt } from '../../lib/format';
import { systemName, useDataSource } from '../../engine/meta/useDataSource';
import { field } from './lib';

// Contract detail header + prepay-accounts tab (ScreenDef header/tab). The generic detail
// screen already renders the lifecycle rail (pending→active→expired, cancelled terminal),
// the editable overview and the Invoices/Time related lists; these add the money-shaped
// summary and the prepay burn-down that span two resources + ledger balances.

interface PrepayBalance {
  remaining_qty: string | number | null;
  purchased_qty: string | number | null;
  consumed_qty: string | number | null;
  /** Money-mode balances from a connected system arrive as exact minor units instead of quantities. */
  remaining_minor?: string;
  purchased_minor?: string;
  consumed_minor?: string;
}
interface PrepayAccount {
  id: string;
  unit: 'hours' | 'money';
  currency: string;
  status?: string;
}

const TOPUP_FIELD = field({ name: 'amount', type: 'money', currencyField: 'currency' });

export function ContractHeader({ row }: ScreenContext) {
  if (!row) return null;
  const currency = String(row.currency ?? 'USD');
  const rel = row.status === 'active' ? formatRelativeDays(row.next_invoice_date as string | null) : null;
  return (
    <div className="hf-stats" aria-label="Contract summary">
      <Stat label="Cycle" value={<span style={{ textTransform: 'capitalize' }}>{String(row.cycle ?? '—')}</span>} icon="clock" />
      <Stat label="Period charge" value={<Money minor={row.period_charge_minor as string} currency={currency} />} icon="receipt" accent />
      <Stat
        label="Next invoice"
        value={row.next_invoice_date ? <span className="hf-num">{String(row.next_invoice_date)}</span> : '—'}
        sub={rel ?? undefined}
        icon="calendar"
      />
    </div>
  );
}

function PrepayRow({ account }: { account: PrepayAccount }) {
  const balance = useApiQuery<PrepayBalance>(['prepay', account.id, 'balance'], `/v1/prepay-accounts/${account.id}/balance`);
  const invalidate = useInvalidateResource();
  const topUp = useAction('prepay-accounts', 'top-up', { toast: { success: 'Prepay topped up' }, invalidate: ['prepay-accounts'] });
  const [open, setOpen] = useState(false);
  const [qty, setQty] = useState('');
  const [minor, setMinor] = useState<number | null>(null);
  const money = account.unit === 'money';

  const b = balance.data;
  const asMoney = b?.remaining_minor !== undefined;
  const purchased = asMoney ? toMinorInt(b?.purchased_minor ?? '0') : Number(b?.purchased_qty ?? 0);
  const consumed = asMoney ? toMinorInt(b?.consumed_minor ?? '0') : Number(b?.consumed_qty ?? 0);
  const frac = purchased > 0 ? consumed / purchased : 0;
  const show = (qty: string | number | null | undefined, minorValue: string | undefined) => (asMoney ? formatMoney(minorValue ?? '0', account.currency) : formatQty(qty));
  // On a connected system of record the top-ups are made there (its invoicing), not here.
  const { overlay } = useDataSource();
  const valid = money ? minor != null && minor > 0 : Number(qty) > 0;

  const submit = () => {
    if (!valid) return;
    topUp.mutate(
      { id: account.id, body: money ? { amount_minor: minor } : { quantity: Number(qty) } },
      {
        onSuccess: () => {
          setOpen(false);
          setQty('');
          setMinor(null);
          void balance.refetch();
          invalidate('prepay-accounts');
        },
      },
    );
  };

  return (
    <div className="hf-card pad hf-stack" style={{ gap: 'var(--s-2)' }}>
      <div className="hf-between">
        <div className="hf-row" style={{ gap: 'var(--s-2)' }}>
          <Badge tone="muted" dot={false}>{account.unit}</Badge>
          {account.status && <StatusBadge status={account.status} />}
        </div>
        {overlay ? (
          <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>Top-ups are made in {systemName(overlay)}</span>
        ) : !open ? (
          <Button size="sm" variant="ghost" icon="plus" onClick={() => setOpen(true)}>Top up</Button>
        ) : (
          <form
            className="hf-row"
            style={{ gap: 'var(--s-2)' }}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {money ? (
              <div style={{ width: 150 }}>
                <MoneyEditor field={TOPUP_FIELD} value={minor} onChange={(v) => setMinor(typeof v === 'number' ? v : null)} currency={account.currency} row={{ currency: account.currency }} autoFocus aria-label="Top-up amount" />
              </div>
            ) : (
              <Input value={qty} onChange={(e) => setQty(e.target.value)} numeric inputMode="decimal" placeholder="Hours" aria-label="Top-up hours" style={{ width: 90 }} autoFocus />
            )}
            <Button size="sm" variant="primary" type="submit" busy={topUp.isPending} disabled={!valid}>Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          </form>
        )}
      </div>
      {balance.isLoading ? (
        <Skeleton lines={1} />
      ) : (
        <dl className="hf-kv">
          <div><dt>Remaining</dt><dd><span className="hf-num">{show(b?.remaining_qty, b?.remaining_minor)}</span></dd></div>
          <div><dt>Purchased</dt><dd><span className="hf-num">{show(b?.purchased_qty, b?.purchased_minor)}</span></dd></div>
          <div><dt>Consumed</dt><dd><span className="hf-num">{show(b?.consumed_qty, b?.consumed_minor)}</span></dd></div>
        </dl>
      )}
      {purchased > 0 && (
        <Progress value={frac} tone={frac >= 1 ? 'neg' : frac > 0.85 ? 'warn' : undefined} title={asMoney ? `${show(null, b?.consumed_minor)} of ${show(null, b?.purchased_minor)} consumed` : `${formatQty(consumed)} of ${formatQty(purchased)} ${account.unit} consumed`} />
      )}
    </div>
  );
}

export function PrepayAccountsTab({ id }: ScreenContext) {
  const list = useResourceList<PrepayAccount>('prepay-accounts', { filter: [{ field: 'contract_id', op: 'eq', value: id }] }, { enabled: Boolean(id) });
  const create = useCreate('prepay-accounts', { toast: { success: 'Prepay account added' } });
  const [unit, setUnit] = useState<'hours' | 'money'>('hours');

  if (list.isLoading) return <Skeleton lines={4} />;
  if (list.error) return <ErrorBanner error={list.error} />;

  return (
    <div className="hf-stack">
      {list.rows.length === 0 ? (
        <EmptyState title="No prepay accounts" hint="Add a prepaid balance for this contract below." icon="wallet" />
      ) : (
        <div className="hf-stack">
          {list.rows.map((a) => (
            <PrepayRow key={a.id} account={a} />
          ))}
        </div>
      )}
      <form
        className="hf-row"
        style={{ alignItems: 'flex-end', gap: 'var(--s-2)' }}
        onSubmit={(e) => {
          e.preventDefault();
          create.mutate({ contract_id: id, unit }, { onSuccess: () => list.refetch() });
        }}
      >
        <Field label="New prepay account" id="prepay-unit">
          <Select id="prepay-unit" value={unit} onChange={(e) => setUnit(e.target.value as 'hours' | 'money')} options={[{ value: 'hours', label: 'Hours' }, { value: 'money', label: 'Money' }]} />
        </Field>
        <Button type="submit" variant="primary" icon="plus" busy={create.isPending}>Add account</Button>
      </form>
    </div>
  );
}
